// src/routes/Services.jsx
import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaArrowRight, FaArrowDown } from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

/*
  THE ARSENAL — the Services page.
  Opens on a giant numbered index (01–06). Each service then gets a
  pinned full-screen CHAPTER: the service name fills with its accent
  color as you scroll (outline → solid), an SVG emblem draws itself,
  deliverable chips cascade in, and the room re-lights per service.
  NOTE for Amiri: all copy lives in the SERVICES array — edit freely.
*/

const SERVICES = [
  {
    id: "websites",
    no: "01",
    name: "Websites",
    accent: "#9281dd",
    tagline: "Cinematic sites that sell while you sleep.",
    pitch:
      "Handcrafted React builds with scroll animations people screenshot. No templates, no page builders — every pixel is written in code and engineered to convert.",
    chips: [
      "Custom React development",
      "Cinematic scroll animations",
      "E-commerce & checkout",
      "Speed-first (loads in under a second)",
      "SEO-ready structure",
    ],
    proof: "Zero templates. Ever.",
    // browser frame
    path: "M15 24 H105 V96 H15 Z M15 40 H105 M24 32 h6 M36 32 h6",
  },
  {
    id: "social-media",
    no: "02",
    name: "Social Media",
    accent: "#e6608a",
    tagline: "Feeds people follow on purpose.",
    pitch:
      "Full management of your presence — strategy, posting, engagement, growth. Platform-native content that looks like it belongs on the feed, not in an ad break.",
    chips: [
      "Monthly content calendar",
      "Daily posting & management",
      "Community engagement",
      "Platform-native formats",
      "Growth reports that make sense",
    ],
    proof: "Audiences built, not bought.",
    // heart
    path: "M60 96 C22 68 14 38 34 26 C49 17 60 30 60 38 C60 30 71 17 86 26 C106 38 98 68 60 96 Z",
  },
  {
    id: "content",
    no: "03",
    name: "Content",
    accent: "#e05656",
    tagline: "Videos people actually finish.",
    pitch:
      "Short-form production built around the hook. We write it before we shoot it — in Albanian, English, or both — and structure every second to hold the thumb.",
    chips: [
      "Hooks written before filming",
      "Shooting & editing",
      "Bilingual scripts (AL / EN)",
      "Reels, TikToks & ad creatives",
      "Viral-first structure",
    ],
    proof: "Millions of organic views delivered.",
    // play button
    path: "M60 14 A46 46 0 1 1 59.9 14 M49 42 L82 60 L49 78 Z",
  },
  {
    id: "marketing",
    no: "04",
    name: "Marketing",
    accent: "#f2b134",
    tagline: "Ads that pay for themselves.",
    pitch:
      "Campaigns on Meta and TikTok with offers engineered before a single euro is spent. Creatives tested against each other, losers cut, winners scaled.",
    chips: [
      "Meta & TikTok campaigns",
      "Offer & funnel strategy",
      "A/B tested creatives",
      "Retargeting flows",
      "ROAS reporting, no vanity metrics",
    ],
    proof: "If it doesn't return, it doesn't run.",
    // rising chart arrow
    path: "M14 100 L44 64 L64 82 L106 28 M88 28 H106 V46",
  },
  {
    id: "systems",
    no: "05",
    name: "Systems",
    accent: "#4ea9d6",
    tagline: "Software that runs the business for you.",
    pitch:
      "Booking systems, admin dashboards, client portals, payment flows — real web apps built end-to-end on React and Firebase, shaped exactly around how you work.",
    chips: [
      "Booking & appointment systems",
      "Admin dashboards",
      "E-commerce backends",
      "Payments (Stripe) & invoicing",
      "Client portals",
    ],
    proof: "Built end-to-end, in-house.",
    // connected nodes
    path: "M25 92 L60 60 L95 88 M60 60 V26 M18 92 A7 7 0 1 0 32 92 A7 7 0 1 0 18 92 M88 88 A7 7 0 1 0 102 88 A7 7 0 1 0 88 88 M53 19 A7 7 0 1 0 67 19 A7 7 0 1 0 53 19",
  },
  {
    id: "branding",
    no: "06",
    name: "Branding",
    accent: "#8fc15a",
    tagline: "Recognizable at a glance.",
    pitch:
      "Identity systems that survive outside the logo file — color, type, templates, packaging. A look people can name before they read a single word.",
    chips: [
      "Logo & visual identity",
      "Color & typography systems",
      "Social media templates",
      "Packaging & print",
      "Brand guidelines",
    ],
    proof: "Remembered by color alone.",
    // ink drop
    path: "M60 14 C60 14 24 56 24 78 A36 36 0 0 0 96 78 C96 56 60 14 60 14 Z",
  },
];

/* ─────────────────────────── CHAPTER ─────────────────────────── */

const Chapter = ({ s, index }) => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const fillRef = useRef(null);
  const pathRef = useRef(null);
  const chipRefs = useRef([]);
  const pitchRef = useRef(null);
  const proofRef = useRef(null);

  useEffect(() => {
    let st;
    const t = setTimeout(() => {
      const path = pathRef.current;
      let len = 0;
      if (path) {
        len = path.getTotalLength();
        gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=140%",
          pin: pinRef.current,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      // the name pours in with color
      tl.fromTo(
        fillRef.current,
        { clipPath: "inset(0 100% 0 0)" },
        { clipPath: "inset(0 0% 0 0)", ease: "none", duration: 1 },
        0
      );
      // the emblem draws itself
      if (path) {
        tl.to(
          path,
          { strokeDashoffset: 0, ease: "none", duration: 1 },
          0
        );
      }
      // copy + chips cascade
      tl.fromTo(
        pitchRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.35 },
        0.05
      );
      tl.fromTo(
        chipRefs.current,
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, stagger: 0.07, duration: 0.3 },
        0.2
      );
      tl.fromTo(
        proofRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.25 },
        0.75
      );

      st = tl.scrollTrigger;
      ScrollTrigger.refresh();
    }, 120);

    return () => {
      clearTimeout(t);
      if (st) st.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChapterSection ref={sectionRef} id={s.id} data-chapter={index}>
      <ChapterPin ref={pinRef} style={{ "--accent": s.accent }}>
        <div className="ch-aura" />
        <span className="ch-ghost">{s.no}</span>

        <div className="ch-grid">
          <div className="ch-left">
            <span className="ch-kicker">
              {s.no} — {s.tagline}
            </span>
            <div className="ch-word">
              {/* base: always-visible outline (JS can never hide this) */}
              <h2 className="ch-outline">{s.name}</h2>
              {/* overlay: pours in with scroll */}
              <h2 className="ch-fill" ref={fillRef}>
                {s.name}
              </h2>
            </div>

            <svg
              className="ch-emblem"
              viewBox="0 0 120 120"
              fill="none"
              aria-hidden="true"
            >
              <path
                ref={pathRef}
                d={s.path}
                stroke={s.accent}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="ch-right">
            <p className="ch-pitch" ref={pitchRef}>
              {s.pitch}
            </p>
            <div className="ch-chips">
              {s.chips.map((c, i) => (
                <span
                  className="ch-chip"
                  key={i}
                  ref={(el) => (chipRefs.current[i] = el)}
                >
                  {c}
                </span>
              ))}
            </div>
            <span className="ch-proof" ref={proofRef}>
              ✦ {s.proof}
            </span>
          </div>
        </div>
      </ChapterPin>
    </ChapterSection>
  );
};

/* ─────────────────────────── PAGE ─────────────────────────── */

const Services = () => {
  const [activeCh, setActiveCh] = useState(0);
  const railRef = useRef(null);

  // rail dot tracking via IntersectionObserver (pin-spacer-proof)
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActiveCh(Number(e.target.dataset.chapter));
          }
        });
      },
      { threshold: 0.4 }
    );
    document
      .querySelectorAll("[data-chapter]")
      .forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Page>
      {/* ── HERO / INDEX ── */}
      <Hero>
        <div className="hero-aura" />
        <span className="hero-kicker">SERVICES — THE ARSENAL</span>
        <h1>
          Everything your brand needs.
          <br />
          <em>Under one roof.</em>
        </h1>
        <p className="hero-sub">
          Six ways we grow brands. Scroll the film — or jump straight to a
          chapter.
        </p>

        <div className="index">
          {SERVICES.map((s, i) => (
            <button
              className="index-row"
              key={s.id}
              onClick={() => goTo(s.id)}
              style={{ "--accent": s.accent, animationDelay: `${0.15 + i * 0.09}s` }}
            >
              <span className="ir-no">{s.no}</span>
              <span className="ir-name">{s.name}</span>
              <span className="ir-tag">{s.tagline}</span>
              <FaArrowDown className="ir-arrow" />
            </button>
          ))}
        </div>
      </Hero>

      {/* ── CHAPTERS ── */}
      {SERVICES.map((s, i) => (
        <Chapter s={s} index={i} key={s.id} />
      ))}

      {/* ── CLOSING CTA ── */}
      <Ending>
        <span className="end-kicker">ROLL CREDITS</span>
        <h2>
          Pick your <em>weapon.</em>
        </h2>
        <p>
          Most brands need two or three of these working together. Tell us
          what you're growing — we'll tell you which.
        </p>
        <a className="end-btn" href="/#contact">
          Let's talk <FaArrowRight />
        </a>
      </Ending>

      {/* ── SIDE RAIL ── */}
      <Rail ref={railRef}>
        {SERVICES.map((s, i) => (
          <button
            key={s.id}
            className={i === activeCh ? "on" : ""}
            style={{ background: i === activeCh ? s.accent : undefined }}
            onClick={() => goTo(s.id)}
            aria-label={s.name}
          />
        ))}
      </Rail>
    </Page>
  );
};

/* ─────────────────────────── STYLES ─────────────────────────── */

const rise = keyframes`
  from { opacity: 0; transform: translateY(36px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  background: #08040c;
  color: #ffffff;
  font-family: "Readex Pro", sans-serif;
`;

const Hero = styled.section`
  position: relative;
  min-height: 100vh;
  padding: 11em 7% 6em;
  overflow: hidden;

  .hero-aura {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% -10%, #33224d55 0%, #08040c 55%);
    pointer-events: none;
  }

  .hero-kicker {
    position: relative;
    font-family: "Montserrat";
    letter-spacing: 0.45em;
    font-size: 0.72rem;
    color: #836caf;
    font-weight: 500;
  }

  h1 {
    position: relative;
    font-weight: 200;
    font-size: clamp(2.2rem, 6vw, 5.6rem);
    line-height: 1.02;
    margin-top: 0.4em;
    em {
      font-style: italic;
      color: #9281dd;
    }
  }

  .hero-sub {
    position: relative;
    color: #8a8595;
    margin-top: 1.2em;
    font-size: clamp(0.95rem, 1.4vw, 1.15rem);
    font-family: "Montserrat";
  }

  .index {
    position: relative;
    margin-top: 4.5em;
    border-top: 1px solid #1d1530;
  }

  .index-row {
    width: 100%;
    display: flex;
    align-items: baseline;
    gap: 1.4em;
    padding: 1.15em 0.4em;
    background: none;
    border: none;
    border-bottom: 1px solid #1d1530;
    cursor: pointer;
    text-align: left;
    color: #ffffff;
    /* pure CSS entrance — can never be stranded hidden */
    animation: ${rise} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
    transition: padding-left 0.35s ease;

    .ir-no {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(0.9rem, 1.6vw, 1.2rem);
      color: #463d6e;
      transition: color 0.35s;
      flex-shrink: 0;
    }
    .ir-name {
      font-family: "Readex Pro", sans-serif;
      font-weight: 300;
      font-size: clamp(1.7rem, 4.6vw, 3.6rem);
      line-height: 1.05;
      transition: color 0.35s;
    }
    .ir-tag {
      flex: 1;
      font-family: "Montserrat";
      font-size: clamp(0.7rem, 1.1vw, 0.88rem);
      color: #6f6a7c;
      transition: color 0.35s;
      text-align: right;
    }
    .ir-arrow {
      color: #463d6e;
      font-size: 0.85rem;
      transition: transform 0.35s ease, color 0.35s ease;
      flex-shrink: 0;
    }

    &:hover {
      padding-left: 1.2em;
      .ir-no,
      .ir-name {
        color: var(--accent);
      }
      .ir-tag {
        color: #b3aec2;
      }
      .ir-arrow {
        color: var(--accent);
        transform: translateY(4px);
      }
    }
  }

  @media (max-width: 700px) {
    padding: 9em 6% 5em;
    .index-row {
      flex-wrap: wrap;
      gap: 0.6em 1em;
      .ir-tag {
        flex-basis: 100%;
        text-align: left;
        order: 4;
      }
    }
  }
`;

const ChapterSection = styled.section`
  position: relative;
  background: #08040c;
`;

const ChapterPin = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;

  .ch-aura {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 28% 55%,
      color-mix(in srgb, var(--accent) 13%, transparent) 0%,
      #08040c 62%
    );
    pointer-events: none;
  }

  .ch-ghost {
    position: absolute;
    right: -2%;
    bottom: -6vh;
    font-weight: 200;
    font-size: clamp(14rem, 34vw, 30rem);
    line-height: 1;
    color: #ffffff05;
    pointer-events: none;
    user-select: none;
  }

  .ch-grid {
    position: relative;
    width: 100%;
    padding: 0 7%;
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    gap: 4vw;
    align-items: center;
  }

  .ch-left {
    position: relative;

    .ch-kicker {
      font-family: "Montserrat";
      letter-spacing: 0.28em;
      font-size: 0.7rem;
      color: var(--accent);
      font-weight: 600;
      text-transform: uppercase;
    }

    .ch-word {
      position: relative;
      margin-top: 0.25em;
      h2 {
        font-weight: 500;
        font-size: clamp(3rem, 9vw, 8.5rem);
        line-height: 1.05;
        white-space: nowrap;
        margin: 0;
      }
      .ch-outline {
        color: transparent;
        -webkit-text-stroke: 1.5px #3a3155;
      }
      .ch-fill {
        position: absolute;
        inset: 0;
        color: var(--accent);
        clip-path: inset(0 100% 0 0);
        will-change: clip-path;
        filter: drop-shadow(0 14px 50px color-mix(in srgb, var(--accent) 35%, transparent));
      }
    }

    .ch-emblem {
      width: clamp(90px, 12vw, 150px);
      height: auto;
      margin-top: 1.6em;
      opacity: 0.9;
    }
  }

  .ch-right {
    .ch-pitch {
      color: #cfcad9;
      font-size: clamp(1rem, 1.5vw, 1.25rem);
      line-height: 1.65;
      max-width: 46ch;
      will-change: transform, opacity;
    }

    .ch-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.6em;
      margin-top: 1.8em;
    }
    .ch-chip {
      font-family: "Montserrat";
      font-size: clamp(0.72rem, 1vw, 0.85rem);
      padding: 0.65em 1.1em;
      border: 1px solid color-mix(in srgb, var(--accent) 45%, transparent);
      border-radius: 30px;
      color: #e6e2f0;
      background: color-mix(in srgb, var(--accent) 8%, transparent);
      will-change: transform, opacity;
    }

    .ch-proof {
      display: block;
      margin-top: 2em;
      font-family: "Montserrat";
      font-size: 0.85rem;
      letter-spacing: 0.06em;
      color: var(--accent);
      will-change: opacity;
    }
  }

  @media (max-width: 860px) {
    .ch-grid {
      grid-template-columns: 1fr;
      gap: 2em;
      align-content: center;
    }
    .ch-left .ch-word h2 {
      font-size: clamp(2.6rem, 13vw, 4.5rem);
    }
    .ch-left .ch-emblem {
      position: absolute;
      right: 0;
      top: -0.5em;
      width: clamp(56px, 16vw, 80px);
      margin-top: 0;
      opacity: 0.55;
    }
    .ch-right .ch-pitch {
      font-size: 0.95rem;
    }
    .ch-ghost {
      font-size: clamp(10rem, 44vw, 16rem);
      bottom: -3vh;
    }
  }
`;

const Ending = styled.section`
  position: relative;
  text-align: center;
  padding: 10em 7% 9em;
  background: radial-gradient(
    circle at 50% 100%,
    #221537 0%,
    #100818 45%,
    #08040c 75%
  );
  overflow: hidden;

  .end-kicker {
    font-family: "Montserrat";
    letter-spacing: 0.45em;
    font-size: 0.72rem;
    color: #836caf;
    font-weight: 500;
  }
  h2 {
    font-weight: 200;
    font-size: clamp(2.6rem, 8vw, 7rem);
    line-height: 1;
    margin: 0.3em 0 0.4em;
    em {
      font-style: italic;
      color: #9281dd;
    }
  }
  p {
    color: #b3aec2;
    max-width: 46ch;
    margin: 0 auto 2.4em;
    font-family: "Montserrat";
    font-size: clamp(0.9rem, 1.3vw, 1.05rem);
    line-height: 1.6;
  }
  .end-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.7em;
    font-family: "Montserrat";
    font-weight: 600;
    font-size: 1rem;
    padding: 17px 36px;
    border-radius: 30px;
    text-decoration: none;
    background: linear-gradient(120deg, #9281dd, #b9c5fb);
    color: #100a18;
    transition: 0.35s;
    svg {
      transition: transform 0.35s ease;
    }
    &:hover {
      box-shadow: 0 0 36px -6px #9281dd;
      transform: translateY(-2px);
      svg {
        transform: translateX(4px);
      }
    }
  }
`;

const Rail = styled.div`
  position: fixed;
  right: 2.2%;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 40;

  button {
    width: 7px;
    height: 7px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #2c2342;
    cursor: pointer;
    transition: 0.4s;
    &.on {
      height: 26px;
      border-radius: 5px;
    }
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

export default Services;
